import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import HomologacionSINFields from '../common/HomologacionSINFields';
// EditServiceModal.tsx — Co Marca — Editar Servicio
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Building2, Tag, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { updateServicio } from '../../services/servicioService';
import { fetchCategories } from '../../services/categoryService';
import { fetchSucursales } from '../../services/pos/sucursalService';
import ImageUpload from '../Upload/ImageUpload';

interface EditServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onServiceUpdated: () => void;
    servicio: any;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#ea8406',
};

const css = `

.esm-modal * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes esmFadeIn  { from{opacity:0;} to{opacity:1;} }
@keyframes esmSlideUp { from{opacity:0;transform:translateY(24px) scale(.98);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes spin { to{transform:rotate(360deg);} }
.esm-overlay { animation:esmFadeIn .22s ease both; }
.esm-dialog  { animation:esmSlideUp .32s cubic-bezier(.16,1,.3,1) both; }
.esm-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid var(--ad-line, #e5e7eb); background:#fafafa;
    font-size:.88rem; color:var(--ad-ink, #374151); outline:none; transition:all .2s;
}
.esm-input:focus { border-color:${colors.secondary}; box-shadow:0 0 0 3px color-mix(in srgb, ${colors.secondary} 7.06%, transparent); background:var(--ad-surface, #fff); }
.esm-select { appearance:none; }
.esm-textarea { resize:vertical; min-height:72px; }
.esm-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.secondary},#004d7a);
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(97, 37, 123,.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.esm-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(97, 37, 123,.34); }
.esm-btn-primary:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; }
.esm-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-soft, #f8fafc); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.esm-btn-ghost:hover { background:var(--ad-line, #f1f5f9); }
.esm-suc-item { transition:background .15s; border-radius:9px; }
.esm-suc-item:hover { background:#f0f9ff; }
.custom-scroll::-webkit-scrollbar { width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97, 37, 123,.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97, 37, 123,.5); }
`;

const EditServiceModal: React.FC<EditServiceModalProps> = ({ isOpen, onClose, onServiceUpdated, servicio }) => {
    const [categorias, setCategorias]             = useState<any[]>([]);
    const [sucursalesDisponibles, setSucs]        = useState<any[]>([]);
    const [sucursalesInactivas, setSucsInactivas] = useState<any[]>([]);
    const [nombre, setNombre]                     = useState('');
    const [descripcion, setDescripcion]           = useState('');
    const [categoria, setCategoria]               = useState('');
    const [estado, setEstado]                     = useState(true);
    const [tipoServicio, setTipoServicio] = useState<'orden_servicio' | 'oficina_virtual'>('orden_servicio');
    const [requiereEspacio, setRequiereEspacio]   = useState(false);
    const [productoSIN, setProductoSIN] = useState<number | null>(null);
    const [unidadSIN, setUnidadSIN] = useState<number | null>(null);
    const [sucursalesSel, setSucursalesSel]       = useState<number[]>([]);
    const [imagen, setImagen]                     = useState<File|null>(null);
    const [isLoading, setIsLoading]               = useState(false);
    const [error, setError]                       = useState<string|null>(null);

    useEffect(() => {
        if (!isOpen) return;
        Promise.all([fetchCategories(), fetchSucursales()]).then(([cats,sucs])=>{
            setCategorias(cats.filter((c:any)=>c.estado));
            setSucs(sucs.filter((s:any)=>s.activo===true));
            if (servicio?.sucursales) {
                setSucsInactivas(sucs.filter((s:any)=>!s.activo && servicio.sucursales.includes(s.id)));
            }
        }).catch((() => undefined));
    }, [isOpen, servicio]);

    useEffect(() => {
        if (isOpen && servicio) {
            setTipoServicio(servicio.tipo_servicio || 'orden_servicio');
            setNombre(servicio.nombre);
            setDescripcion(servicio.descripcion||'');
            setCategoria(servicio.categoria.toString());
            setEstado(servicio.estado);
            setRequiereEspacio(servicio.requiere_espacio||false);
            setProductoSIN(servicio.producto_servicio_sin || null);
            setUnidadSIN(servicio.unidad_medida_sin || null);
            setSucursalesSel(servicio.sucursales||[]);
            setImagen(null); setError(null);
        }
    }, [isOpen, servicio]);

    const toggleSuc = (id: number) =>
        setSucursalesSel(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (sucursalesSel.length===0) { setError('Debe seleccionar al menos una sucursal.'); return; }
        setIsLoading(true); setError(null);
        try {
            await updateServicio(servicio.id, { categoria:Number(categoria), tipo_servicio:tipoServicio, nombre, descripcion, imagen, estado, requiere_espacio:requiereEspacio, sucursales:sucursalesSel, producto_servicio_sin:productoSIN, unidad_medida_sin:unidadSIN });
            onServiceUpdated(); onClose();
        } catch { setError('Error al actualizar el servicio. Por favor intente nuevamente.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen || !servicio) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="esm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="esm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                <LumaAccessiblePanel coworkTitle="Editar · Servicios" data-luma-dialog="true" className="esm-dialog custom-scroll card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'24px', width:'100%', maxWidth:'620px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid var(--ad-line, #f1f5f9)' }}>

                    {/* Barra top — azul para editar */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px color-mix(in srgb, ${colors.secondary} 18.82%, transparent)` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <LumaAccessibleTitle style={{ fontSize:'1.1rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Editar Servicio</LumaAccessibleTitle>
                                    <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500' }}>Modificando <strong style={{ color:'var(--ad-ink, #374151)' }}>{servicio.nombre}</strong></p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'}>
                            <X size={15} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>

                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`color-mix(in srgb, ${colors.primary} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:'var(--ad-link, #2854c5)', fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Categoría + Nombre */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Tag size={11} color={colors.accentDark}/> Categoría *
                                </label>
                                <select required value={categoria} onChange={e=>{ setCategoria(e.target.value); setProductoSIN(null); setUnidadSIN(null); }} className="esm-input esm-select">
                                    <option value="">Seleccionar categoría</option>
                                    {categorias.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Nombre *</label>
                                <input type="text" required value={nombre} onChange={e=>setNombre(e.target.value)} className="esm-input" placeholder="Nombre del servicio"/>
                            </div>
                        </div>

                        <HomologacionSINFields className="esm-input esm-select" producto={productoSIN} unidad={unidadSIN} actividad={categorias.find(c => c.id === Number(categoria))?.actividad_economica_codigo} onChange={(producto, unidad) => { setProductoSIN(producto); setUnidadSIN(unidad); }} />

                        {/* Descripción + Imagen */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Descripción</label>
                                <textarea value={descripcion} onChange={e=>setDescripcion(e.target.value)} className="esm-input esm-textarea" placeholder="Descripción opcional..."/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Imagen</label>
                                <ImageUpload setImagen={setImagen} currentImage={servicio.imagen}/>
                            </div>
                        </div>

                        {/* Sucursales activas */}
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'10px' }}>
                                <Building2 size={11} color={colors.secondary}/> Sucursales activas
                                {sucursalesSel.length>0 && <span style={{ marginLeft:'auto', fontSize:'.68rem', fontWeight:'700', color:'var(--ad-link, #2854c5)', background:`color-mix(in srgb, ${colors.secondary} 7.06%, transparent)`, borderRadius:'100px', padding:'1px 8px' }}>{sucursalesSel.length} seleccionadas</span>}
                            </label>
                            {sucursalesDisponibles.length===0 ? (
                                <div style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'12px', padding:'16px', textAlign:'center', color:'var(--ad-muted, #94a3b8)', fontSize:'.82rem' }} className="card custom-card vx-card">No hay sucursales activas disponibles</div>
                            ) : (
                                <div className="custom-scroll card custom-card vx-card" style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'12px', maxHeight:'140px', overflowY:'auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                                    {sucursalesDisponibles.map(suc=>{
                                        const checked = sucursalesSel.includes(suc.id);
                                        return (
                                            <label key={suc.id} className="esm-suc-item" style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', cursor:'pointer' }}>
                                                <div onClick={()=>toggleSuc(suc.id)} style={{ width:'18px', height:'18px', borderRadius:'5px', border:`1.5px solid ${checked?colors.secondary:'#d1d5db'}`, background:checked?colors.secondary:'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .15s', cursor:'pointer' }}>
                                                    {checked&&<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                </div>
                                                <input type="checkbox" checked={checked} onChange={()=>toggleSuc(suc.id)} style={{ display:'none' }}/>
                                                <span style={{ fontSize:'.82rem', color:'var(--ad-ink, #374151)', fontWeight:checked?'700':'500' }}>{suc.nombre} <span style={{ color:'var(--ad-muted, #94a3b8)', fontSize:'.72rem' }}>({suc.codigo})</span></span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Sucursales inactivas asignadas */}
                        {sucursalesInactivas.length>0 && (
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'650', color:colors.accentDark, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'8px' }}>
                                    <AlertTriangle size={11}/> Sucursales inactivas asignadas
                                </label>
                                <div style={{ background:`color-mix(in srgb, ${colors.accentDark} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.accentDark} 13.33%, transparent)`, borderRadius:'12px', padding:'12px 14px' }} className="card custom-card vx-card">
                                    <p style={{ fontSize:'.75rem', color:colors.accentDark, fontWeight:'600', marginBottom:'8px' }}>No se pueden modificar desde aquí. Activa la sucursal para gestionarla.</p>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                                        {sucursalesInactivas.map(s=>(
                                            <div key={s.id} style={{ display:'flex', alignItems:'center', gap:'7px', opacity:.7 }}>
                                                <div style={{ width:'16px', height:'16px', borderRadius:'4px', border:'1.5px solid #d1d5db', background:'var(--ad-line, #f1f5f9)', flexShrink:0 }}/>
                                                <span style={{ fontSize:'.78rem', color:'var(--ad-muted, #94a3b8)', textDecoration:'line-through' }}>{s.nombre} ({s.codigo})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <label>Tipo de servicio
                            <select className="esm-input" value={tipoServicio} onChange={e => {setTipoServicio(e.target.value as 'orden_servicio' | 'oficina_virtual'); setRequiereEspacio(false);}}>
                                <option value="orden_servicio">Orden de servicio</option>
                                <option value="oficina_virtual">Oficina virtual</option>
                            </select>
                        </label>
                        {/* Requiere espacio + Estado */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                            {[
                                { label:'¿Requiere espacio físico?', val:requiereEspacio, setter:setRequiereEspacio, desc:requiereEspacio?'Necesita espacio asignado':'Sin espacio específico' },
                                { label:'Estado del servicio', val:estado, setter:setEstado, desc:estado?'● Activo':'○ Inactivo' },
                            ].map(item=>(
                                <div key={item.label} style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }} className="card custom-card vx-card">
                                    <div>
                                        <p style={{ fontSize:'.82rem', fontWeight:'700', color:'var(--ad-ink, #0f172a)', marginBottom:'2px' }}>{item.label}</p>
                                        <p style={{ fontSize:'.7rem', color:item.val?colors.secondaryLight:'var(--ad-muted, #94a3b8)', fontWeight:'600' }}>{item.desc}</p>
                                    </div>
                                    <div onClick={()=>{if(item.setter === setRequiereEspacio && tipoServicio === 'oficina_virtual') return; item.setter(!item.val);}} style={{ width:'44px', height:'24px', borderRadius:'100px', background:item.val?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                        <div style={{ position:'absolute', top:'3px', left:item.val?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'var(--ad-surface, #fff)', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="esm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading||sucursalesSel.length===0} className="esm-btn-primary btn btn-primary">
                                {isLoading
                                    ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Actualizando...</>
                                    : <><CheckCircle2 size={14}/> Actualizar Servicio</>
                                }
                            </button>
                        </div>
                    </form>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default EditServiceModal;




